import { Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material"
import { useFindMissionSensorList } from "../../api/MissionEditApi"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { DatePickerCustom } from "../../components/Fields/DatePickerCustom"
import { EmployeeField } from "../../components/Fields/EmployeeField"
import { NexRadioGroup } from "../../components/Fields/RadioGroup"
import { useMissions } from "../../hooks/useMissions"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { WeatherRecording, weatherRecordingOptions, weatherRecordingValues } from "../../models/FlightConditions"
import { SensorType } from "../../models/sensors/Sensor"
import { MissionType, PlatformType } from "../../store/types/MissionsTypes"
import { sortByField } from "../../tools"
import { removeOrAddCheckboxArrayEntry } from "../../utils/ArrayUtils"
import { TimeUtils } from "../../utils/TimeUtils"
import { teamRadioOptions } from "./model"

type EmployeeFieldType = {
    label: string
    field: string
    employeeRole: "pilots" | "operators" | "managers"
    required?: boolean
}

const sensorFields: { label: string; field: keyof MissionType; required?: boolean }[] = [
    { label: "Primary Sensor", field: "sensor_id_1", required: true },
    { label: "Secondary Sensor", field: "sensor_id_2" },
]

const employeeFields: EmployeeFieldType[] = [
    { label: "Sensor Operator 1", field: "operator_1_id", employeeRole: "operators", required: true },
    { label: "Sensor Operator 2", field: "operator_2_id", employeeRole: "operators" },
    { label: "Pilot 1", field: "pilot_1_id", employeeRole: "pilots", required: true },
    { label: "Pilot 2", field: "pilot_2_id", employeeRole: "pilots" },
    { label: "Manager On Call", field: "manager_id", employeeRole: "managers", required: true },
]

export const TeamIdentification = () => {
    const projectNumber = useNexusProjectNumber()

    const { missions, handleMissionDataChange, handleVendor } = useMissions()
    const { selectedMission, isVendorFlow, platformList, employeeList, vendorNameList, airportList } = missions
    const { platforms, project_platforms } = platformList

    // Error handling is in the MissionEditorWrapper
    const sensorListResponse = useFindMissionSensorList(projectNumber)
    const sensorListResult = sensorListResponse?.data
    const sensors = sensorListResult?.sensors ?? []
    const project_sensors = sensorListResult?.project_sensors ?? []

    const handleWeatherRecordingUpdate = (weather: WeatherRecording, checked: boolean) => {
        const newWeatherRecording = removeOrAddCheckboxArrayEntry(
            selectedMission.weather_recording_selections,
            weather,
            checked,
        )
        handleMissionDataChange({ weather_recording_selections: newWeatherRecording })
    }

    return (
        <Stack gap={2}>
            <Typography variant={"h6"}>Sensors</Typography>
            {sensorFields.map((sensor, idx) => (
                <NexAutocomplete
                    key={idx}
                    value={
                        sensors.find(
                            (s: SensorType) => s.sensor_id === selectedMission[sensor.field as keyof MissionType],
                        ) || null
                    }
                    options={sortByField(sensors, "sensor_id", project_sensors)}
                    groupBy={(opt: SensorType) =>
                        project_sensors.includes(opt?.sensor_id || -1) ? "Used in this Project" : "All Sensors"
                    }
                    getOptionLabel={(opt: SensorType) => `${opt.sensor_model} - ${opt.serial_number}`}
                    renderOption={(props, opt) => (
                        <li {...props} key={opt.sensor_id}>
                            {opt.sensor_model} - {opt.serial_number}
                        </li>
                    )}
                    onChange={(e, value) => handleMissionDataChange({ [sensor.field]: value?.sensor_id ?? null })}
                    TextFieldProps={{ required: sensor.required, ...sensor }}
                />
            ))}

            <Typography variant={"h6"}>Weather Recording</Typography>
            <FormGroup>
                {weatherRecordingOptions.map((weatherRecordingOpt) => (
                    <FormControlLabel
                        key={weatherRecordingOpt}
                        label={weatherRecordingValues[weatherRecordingOpt].displayLabel}
                        control={
                            <Checkbox
                                size="small"
                                checked={selectedMission.weather_recording_selections.includes(weatherRecordingOpt)}
                                onChange={(_, checked) => handleWeatherRecordingUpdate(weatherRecordingOpt, checked)}
                            />
                        }
                    />
                ))}
            </FormGroup>

            <Typography variant={"h6"}>Activity Date</Typography>
            <DatePickerCustom
                label="Select"
                value={TimeUtils.parseDate(selectedMission.flown_date)}
                onChange={(date) => {
                    handleMissionDataChange({ flown_date: TimeUtils.formatDate(date) })
                }}
                helperText="Required"
                dataTestId={"activity-date-picker"}
            />

            <Typography variant={"h6"}>Aircraft/Platform</Typography>
            <NexAutocomplete
                data-testid={"mission-aircraft-platform-select"}
                TextFieldProps={{ label: "Select", required: !isVendorFlow }}
                value={platforms.find((p) => p.platform_id === selectedMission.platform_id) || null}
                groupBy={(opt: PlatformType) =>
                    project_platforms.includes(opt.platform_id!) ? "Used in this Project" : "All Platforms"
                }
                options={sortByField(platforms, "platform_id", project_platforms)}
                getOptionLabel={(opt: PlatformType) => opt.platform_name ?? ""}
                renderOption={(props, opt) => (
                    <li {...props} key={opt.platform_id}>
                        {opt.platform_name}
                    </li>
                )}
                onChange={(e, value) => handleMissionDataChange({ platform_id: value?.platform_id ?? null })}
                width="sm"
            />

            <Typography variant={"h6"}>Team Members</Typography>
            <NexRadioGroup
                value={teamRadioOptions.find((option) => option.value === isVendorFlow)?.id}
                options={teamRadioOptions}
                onChange={(option: (typeof teamRadioOptions)[0]) => handleVendor(option.value)}
            />
            {isVendorFlow && (
                <NexAutocomplete
                    TextFieldProps={{ label: "Team Name", required: true }}
                    value={selectedMission.vendor_name || null}
                    options={vendorNameList}
                    onChange={(e, value) => handleMissionDataChange({ vendor_name: value ?? null })}
                    freeSolo
                    forcePopupIcon
                />
            )}
            {employeeFields.slice(isVendorFlow ? 4 : 0).map((employee, idx) => (
                <EmployeeField
                    key={idx}
                    employeeId={selectedMission[employee.field as keyof MissionType] as string}
                    employeeList={employeeList}
                    handleChange={(value) => handleMissionDataChange({ [employee.field]: value })}
                    {...employee}
                    width={"md"}
                />
            ))}

            <Typography variant={"h6"}>Base Airport</Typography>
            <NexAutocomplete
                TextFieldProps={{
                    label: "Select",
                    required: !isVendorFlow,
                    inputProps: { sx: { textTransform: "uppercase" } },
                }}
                value={selectedMission.base_airport || null}
                options={[
                    ...airportList.project_airports,
                    ...airportList.airports.filter((ap) => !airportList.project_airports.includes(ap)),
                ]}
                groupBy={(opt: string) =>
                    airportList.project_airports.includes(opt) ? "Used in this Project" : "All Airports"
                }
                onChange={(e, value) => handleMissionDataChange({ base_airport: value?.toUpperCase() ?? null })}
                freeSolo
                forcePopupIcon
                maxLength={4}
            />
        </Stack>
    )
}
